import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import Reveal from "@/components/landing/Reveal";
import { LIVE_APP_URL } from "@/components/landing/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Name", type: "text", required: true, testid: "input-name", placeholder: "Max Mustermann" },
  { name: "organisation", label: "Organisation", type: "text", required: false, testid: "input-organisation", placeholder: "Firma / Kommune / Versicherung ..." },
  { name: "email", label: "E-Mail", type: "email", required: true, testid: "input-email", placeholder: "name@organisation.de" },
];

export default function Footer() {
  const [form, setForm] = useState({ name: "", organisation: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Anfrage übermittelt — wir melden uns bei dir.");
      setForm({ name: "", organisation: "", email: "", message: "" });
    } catch {
      toast.error("Übermittlung fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer id="kontakt" data-testid="contact-section" className="relative bg-[#060607]">
      <div className="grid border-b border-white/10 lg:grid-cols-2">
        <div className="flex flex-col justify-between border-white/10 px-6 py-20 max-lg:border-b lg:px-16 lg:py-28">
          <Reveal>
            <img
              src="/assets/logo.png"
              alt="DROPS TFR Wappen — Schnell. Technisch. Zuverlässig."
              className="mb-12 w-40 object-contain lg:w-52"
            />
            <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">KONTAKT</p>
            <h2 className="mt-6 font-display text-[18vw] font-black uppercase leading-[0.82] tracking-tighter lg:text-[8.5vw]">
              Jetzt
              <br />
              <span className="text-stroke-gold">Anfragen.</span>
            </h2>
            <p className="mt-10 max-w-sm font-tac text-sm leading-relaxed text-[#A1A1AA]">
              Du brauchst ein Lagebild, eine Inspektion oder eine lückenlose
              Dokumentation? Schreib uns — wir melden uns einsatznah.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-14">
            <a
              data-testid="footer-live-app-link"
              href={LIVE_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 font-tac text-xs tracking-[0.25em] text-white/70 transition-colors duration-300 hover:text-white"
            >
              <span className="h-2 w-2 bg-[#F5B301] transition-transform duration-300 group-hover:rotate-45" />
              LIVE-APP: FIELD-REPORT-11.EMERGENT.HOST
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>
        </div>

        <div className="px-6 py-20 lg:px-16 lg:py-28">
          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={handleSubmit} className="flex flex-col gap-9">
              {FIELDS.map((field) => (
                <label key={field.name} className="group block">
                  <span className="font-tac text-[10px] uppercase tracking-[0.3em] text-white/50 transition-colors duration-300 group-focus-within:text-[#F5B301]">
                    {field.label}
                    {field.required && " *"}
                  </span>
                  <input
                    data-testid={field.testid}
                    type={field.type}
                    required={field.required}
                    placeholder={field.placeholder}
                    value={form[field.name]}
                    onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                    className="mt-2 w-full border-b border-white/20 bg-transparent py-3 font-tac text-sm text-white placeholder:text-white/25 focus:border-[#F5B301] focus:outline-none"
                  />
                </label>
              ))}

              <label className="group block">
                <span className="font-tac text-[10px] uppercase tracking-[0.3em] text-white/50 transition-colors duration-300 group-focus-within:text-[#F5B301]">
                  Nachricht *
                </span>
                <textarea
                  data-testid="input-message"
                  required
                  rows={4}
                  placeholder="Worum geht es bei deinem Einsatz?"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full resize-none border-b border-white/20 bg-transparent py-3 font-tac text-sm text-white placeholder:text-white/25 focus:border-[#F5B301] focus:outline-none"
                />
              </label>

              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="group relative mt-2 w-fit overflow-hidden bg-[#F5B301] px-10 py-4 font-tac text-xs font-bold tracking-[0.25em] text-white transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98] disabled:opacity-60"
              >
                <span className="absolute inset-0 -translate-x-full bg-white/20 transition-transform duration-300 group-hover:translate-x-0" />
                <span className="relative z-10 flex items-center gap-3">
                  {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {sending ? "WIRD GESENDET ..." : "ANFRAGE SENDEN"}
                </span>
              </button>
            </form>
          </Reveal>
        </div>
      </div>

      <div className="flex flex-col items-center border-b border-white/10 px-6 py-20 lg:py-24">
        <Reveal>
          <p className="mb-10 text-center font-tac text-[10px] tracking-[0.4em] text-white/40">
            EINE MARKE VON
          </p>
          <img
            data-testid="footer-brand-logo"
            src="/assets/drops-main.png"
            alt="DROPS — Drohnen Service und Technik"
            className="w-72 object-contain transition-transform duration-500 hover:scale-[1.03] lg:w-[420px]"
          />
        </Reveal>
      </div>

      <div className="flex flex-col gap-4 px-6 py-8 font-tac text-[10px] tracking-[0.25em] text-white/40 lg:flex-row lg:items-center lg:justify-between lg:px-16">
        <span>© 2026 DROPS TFR — TECHNICAL FIRST RESPONDER</span>
        <span className="text-[#F5B301]/70">SCHNELL. TECHNISCH. ZUVERLÄSSIG.</span>
        <div className="flex items-center gap-8">
          <a data-testid="footer-impressum-link" href="/impressum" className="transition-colors duration-300 hover:text-white">
            IMPRESSUM
          </a>
          <a data-testid="footer-datenschutz-link" href="/datenschutz" className="transition-colors duration-300 hover:text-white">
            DATENSCHUTZ
          </a>
        </div>
      </div>
    </footer>
  );
}
